import { Injectable } from '@angular/core';
import { LoggerService } from '@app/logger/logger.service';
import { ApiBaseService } from './api-base.service';
import {
  SupportTicket,
  CreateSupportTicketPayload,
  UpdateSupportTicketPayload,
  TicketStatus,
} from '@shared/models/entities/supportTicket.model';

const log = new LoggerService('SUPPORT TICKETS SERVICE');

/**
 * Tickets de soporte. Recurso REST `supportTickets`:
 *   POST   /supportTickets/search
 *   POST   /supportTickets
 *   PATCH  /supportTickets
 *   DELETE /supportTickets
 *
 * Sigue el patrón de `permissions.service.ts`: payloads tipados,
 * `operationType: 'single'` y devolviendo `response.data[0]`.
 */
@Injectable({ providedIn: 'root' })
export class SupportTicketsService extends ApiBaseService<SupportTicket> {

  constructor() {
    super('supportTickets');
    log.debug('Servicio de Tickets de Soporte inicializado');
  }

  /**
   * Lista los tickets. Si se pasa `requestedBy`, solo los de ese usuario
   * (es lo que ve cualquier rol que no sea Administrador Epione).
   */
  async list(requestedBy?: string | null): Promise<SupportTicket[]> {
    const condition: Record<string, unknown> = { deleted: false };
    if (requestedBy) condition['requestedBy'] = requestedBy;

    try {
      await this.search({ searchType: 'default', condition });
    } catch (err: any) {
      // El backend lanza NotFoundError (404) cuando no hay ni un registro:
      // para una bandeja vacía eso no es un error, es "todavía no hay tickets".
      if (err?.status === 404) {
        this.listData.set([]);
        return [];
      }
      throw err;
    }
    return this.listData();
  }

  /** Crea un ticket. El backend genera folio, fecha y estado inicial. */
  async createTicket(payload: CreateSupportTicketPayload): Promise<SupportTicket | null> {
    const body: CreateSupportTicketPayload = {
      operationType: 'single',
      ...payload,
      subject:     payload.subject.trim(),
      description: payload.description.trim(),
      location:    payload.location?.trim() || null,
    };
    const response = await this.create(body as unknown as SupportTicket);
    return Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null;
  }

  /** Actualiza un ticket (acciones del administrador). */
  async updateTicket(payload: UpdateSupportTicketPayload): Promise<SupportTicket | null> {
    if (!payload.id) throw new Error('updateTicket requiere id.');

    const body: UpdateSupportTicketPayload = { operationType: 'single', ...payload };
    const response = await this.update(body as unknown as SupportTicket);
    return Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null;
  }

  /**
   * Cambia el estado del ticket.
   *
   * El sello `resolvedOn` lo pone el backend (hook `before_update` de
   * `supportTickets`), no el navegador: así la hora es la del servidor y la
   * regla vale aunque el PATCH venga de otro cliente.
   */
  async changeStatus(id: string, status: TicketStatus): Promise<SupportTicket | null> {
    return this.updateTicket({ id, status });
  }

  /** Borrado lógico. */
  async softDeleteTicket(id: string): Promise<SupportTicket | null> {
    return this.updateTicket({ id, deleted: true });
  }
}
